<template>
  <div>
    <alert
      class="mb3"
      v-if="hasMessage"
      :message="message.content"
      :type="message.type"
      @close="message = {}" />

    <template v-if="!finished">
      <v-form
        @submit="submit"
        :initial-values="getInitialValues()">
        <div
          v-for="field in fields"
          :key="field.key"
          class="ce-fieldWrapper"
          :class="[`ce-fieldWrapper--${field.type}`]">
          <template v-if="field.type === 'input'">
            <validated-input
              :name="field.key"
              :label="field.label"
              :disabled="typeof field.disabled === 'boolean' ? field.disabled : false"
              :rules="field.rules ? field.rules : ''" />
          </template>

          <template v-if="field.type === 'select' && (!field.multi || field.multi !== true)">
            <validated-select
              :name="field.key"
              :label="field.label"
              :disabled="typeof field.disabled === 'boolean' ? field.disabled : false"
              :rules="field.rules ? field.rules : ''"
              :options="field.options"
              :show-empty-default="field.emptyDefault"
            />
          </template>
          <template v-if="field.type === 'select' && field.multi === true">
            <validated-multiselect
              :name="field.key"
              :label="field.label"
              :disabled="typeof field.disabled === 'boolean' ? field.disabled : false"
              :rules="field.rules ? field.rules : ''"
              :options="field.options"
              :show-empty-default="field.emptyDefault"
              :show-other-option="typeof field.showOtherOption === 'boolean' ? field.showOtherOption : false"
            />
          </template>

          <div
            v-if="field.type === 'label'"
            class="ce-field ce-labelField">
            {{ field.content }}
          </div>

          <div
            v-if="field.type === 'section_header'"
            class="ce-field ce-sectionHeader">
            {{ field.content }}
          </div>
        </div>

        <div
          v-if="consentHintNecessary"
          class="ms-1 line-height-4">
          <i18n-t
            keypath="form.consentHint"
            tag="p"
            scope="global">
            <a
              :href="links.sunChemicalDataProtectionPage ?? links.dataProtectionPage"
              rel="noopener noreferrer">
              {{ $t('form.dataProtectionPage') }}</a>
          </i18n-t>
        </div>

        <div class="mt4">
          <button
            class="ce-btn ce-btn-light ce-btn--fullWidth"
            type="submit">
            <template v-if="isSending">
              <fa-icon
                icon="spinner"
                spin />
            </template>
            <template v-else>
              {{ $t('form.submitCta') }}
            </template>
          </button>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import axios from 'axios';
import Alert from 'components/Alert.vue';

/*
  TODO:
   * component for table select / multiple choice like?!
   * two fields in one row?
 */
export default {
  props: {
    config: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isSending: false,
      message: {},
    };
  },
  components: { Alert },
  computed: {
    ...mapState('user', {
      user: 'activeUser'
    }),
    ...mapState('generic', ['formSecurityToken', 'links']),
    consentHintNecessary() {
      return !(this.user
          && this.user.consent
          && Array.isArray(this.user.consent)
          && this.user.consent.indexOf('inquiries') > -1);
    },
    hasMessage() {
      return this.message && this.message.content;
    },
    fields() {
      const fields = this.$cloneObject(this.config.fields);
      fields.forEach((field) => {
        if (field.disabledRule) {
          // eslint-disable-next-line no-new-func
          const func = Function(field.disabledRule).bind(this);
          field.disabled = func();
        }
      });

      return fields;
    },
    finished() {
      return this.message && this.message.type === 'success';
    }
  },
  methods: {
    hasField(fieldKey) {
      return this.fields.find((field) => field.key === fieldKey);
    },
    getSubmitPayload(values) {
      const data = { ...values };

      const payload = {};
      this.fields.forEach((field) => {
        const { key } = field;

        if (data[key] && data[key] !== '') {
          let value = data[key];

          if (field.type === 'select' && field.multi === true && Array.isArray(value)) {
            value = value.map((item) => (item.customInput ? item.customInput : item.value));
          }

          payload[key] = value;
        }
      });

      return payload;
    },
    getInitialValues() {
      const values = {};

      if (this.user) {
        ['email', 'firstName', 'lastName', 'company'].forEach((field) => {
          if (this.hasField(field) && this.user[field]) {
            values[field] = this.user[field];
          }
        });
      }

      return values;
    },
    async submit(values, ctx) {
      this.isSending = true;

      const data = {
        formSecurityToken: this.formSecurityToken,
        formId: this.config.formId,
        form: {
          ...this.getSubmitPayload(values)
        }
      };

      try {
        await axios.post(`${location.origin}/api/v1/message/form`, data);
        this.$emit('finished');

        this.isSending = false;

        ctx.resetForm({
          ...this.getInitialValues(),
        });

        this.message = {
          type: 'success',
          content: this.$t('contactForm.finishedMessage')
        };
      } catch (e) {
        this.message = {
          type: 'error',
          content: e.response?.data?.message ?? e.message
        };
      }

      this.isSending = false;
    }
  }
};
</script>

<style lang="less">
@import (reference) '~styles/includes/base';

.ce-fieldWrapper--label {
  &:not(:first-child) {
    margin-top: @space-5;
  }
}

.ce-fieldWrapper--section_header {
  .ms(3);
  font-weight: 700;
  &:not(:first-child) {
    margin-top: @space-5;
  }

  + .ce-fieldWrapper--label {
    margin-top: 0;
  }
}

.ce-fieldWrapper--spacer {
  margin-top: @space-5;
}
</style>
